import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { obtenerListaCotizaciones } from "../../../store/cotizaciones/thunkCotizaciones";
import Select from "react-select";
import Collapse from "@mui/material/Collapse";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { UnificarModales } from "../Modales/UnificarModales";
import { DetalleCotizacionModal } from "./DetalleCotizacionModal";
import { useNavigate } from "react-router-dom";

export const ListaCotizacion = () => {
    // Hook para manejar la navegación en la aplicación
    const navigate = useNavigate();

    // Estados del componente para manejar datos, filtros, modales y paginación
    const [dataTable, setDataTable] = useState([]); // Almacena los datos completos de la tabla
    const [filteredData, setFilteredData] = useState([]); // Almacena los datos filtrados según los filtros aplicados
    const [loading, setLoading] = useState(false); // Indica si se están cargando datos
    const [pageSize, setPageSize] = useState(20); // Tamaño de página para la paginación
    const [currentPage, setCurrentPage] = useState(1); // Página actual para la paginación
    const [openFilters, setOpenFilters] = useState(false); // Estado para abrir/cerrar el panel de filtros
    const [openModal, setOpenModal] = useState(false); // Estado para abrir/cerrar el modal
    const [selectedData, setSelectedData] = useState(null); // Datos seleccionados al hacer clic en una fila
    const [filters, setFilters] = useState({
        nombre_evento_coti: "", // Filtro por nombre del evento
        id_consecutivo_coti: "", // Filtro por ID consecutivo
        nombre_empresa: null, // Filtro por nombre de la empresa
        name_admin: null, // Filtro por administrador
        Estado_coti: null, // Filtro por estado de la cotización
    });

    // Hook para acceder al dispatcher de Redux
    const dispatch = useDispatch();

    // Configuración de columnas para la tabla
    const columns = [
        { key: "id_consecutivo_coti", header: "ID Consecutivo" },
        { key: "nombre_evento_coti", header: "Nombre del Evento" },
        { key: "nombre_empresa", header: "Nombre de la Empresa" },
        { key: "name_admin", header: "Nombre del Admin" },
        { key: "fecha_evento_coti", header: "Fecha del Evento" },
        { key: "fecha_creacion_coti", header: "Fecha de Creación" },
        { key: "Estado_coti", header: "Estado de la Cotización" },
    ];

    // Función para obtener la lista de cotizaciones desde la API
    const fetchCotizaciones = useCallback(
        async (estado) => {
            setLoading(true); // Activa el estado de carga
            try {
                // Filtra por estado si es necesario
                const orSql = estado === 1 ? "where Estado_coti !='Confirmada'" : "";
                // Llama a la acción de Redux para obtener los datos
                const response = await dispatch(obtenerListaCotizaciones(orSql));

                setDataTable(response); // Actualiza la tabla con los datos obtenidos
                setFilteredData(response); // Inicializa los datos filtrados
            } catch (error) {
                console.error("Error al obtener la lista de cotizaciones:", error);
            } finally {
                setLoading(false); // Desactiva el estado de carga
            }
        },
        [dispatch], // Dependencia para evitar llamadas innecesarias
    );

    // Efecto para cargar las cotizaciones al montar el componente
    useEffect(() => {
        if (!dataTable.length) {
            fetchCotizaciones(1); // Obtiene cotizaciones pendientes
        }
    }, [dataTable]);

    // Maneja cambios en los filtros del formulario
    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value, // Actualiza el filtro correspondiente
        }));
    };

    // Efecto para aplicar filtros a los datos
    useEffect(() => {
        const filtered = dataTable.filter((row) => {
            return (
                (filters.nombre_evento_coti === "" || row.nombre_evento_coti.toLowerCase().includes(filters.nombre_evento_coti.toLowerCase())) &&
                (filters.id_consecutivo_coti === "" || row.id_consecutivo_coti.toString().includes(filters.id_consecutivo_coti)) &&
                (filters.nombre_empresa === null || row.nombre_empresa === filters.nombre_empresa.value) &&
                (filters.name_admin === null || row.name_admin === filters.name_admin.value) &&
                (filters.Estado_coti === null || row.Estado_coti === filters.Estado_coti.value)
            );
        });
        setFilteredData(filtered); // Actualiza los datos filtrados
    }, [filters, dataTable]);

    // Obtiene opciones únicas para un filtro específico
    const uniqueOptions = (key) => [...new Set(dataTable.map((item) => item[key]))].map((value) => ({ value, label: value }));

    // Calcula el número total de páginas para la paginación
    const totalPages = Math.ceil(filteredData.length / pageSize);

    // Maneja el cambio de tamaño de página
    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setCurrentPage(1); // Reinicia a la primera página
    };

    // Cambia la página actual
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Maneja el clic en una fila de la tabla
    const handleRowClick = (row) => {
        setSelectedData(row); // Guarda los datos seleccionados
        setOpenModal(true); // Abre el modal
    };

    // Cierra el modal y limpia los datos seleccionados
    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedData(null);
    };

    // Obtiene los datos de la página actual para mostrar en la tabla
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentData = filteredData.slice(startIndex, endIndex);

    // Formatea la fecha de modificación para mostrarla de forma amigable
    const formatearFechaModi = (fechas) => {
        var fecha = new Date(fechas);
        var opciones = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
        var fechaFormateada = fecha.toLocaleDateString("es-ES", opciones);
        return fechaFormateada;
    };

    // Mantiene la fecha del evento tal como está (ejemplo de formato pendiente)
    const formatearFechaEvento = (fecha) => {
        return fecha;
    };

    /**
     * Navega a la página de creación de cotización con datos iniciales.
     */
    const generarCotizacion = () => {
        navigate(`/cotizaciones/crear?data=0`); // Redirige a la página de creación
    };

    const getEstadoStyle = (estado) => {
        switch (estado) {
            case "Confirmada":
                return { color: "green", fontWeight: "bold" };
            case "Pendiente":
                return { color: "goldenrod", fontWeight: "bold" };
            case "Enviado":
                return { color: "blue", fontWeight: "bold" };
            case "Tentativa":
                return { color: "gray", fontWeight: "bold" };
            default:
                 return { color: "red", fontWeight: "bold" };
        }
    };

    return (
        <div>
            <h2>Lista de Cotizaciones</h2>

            <Button variant="contained" onClick={() => setOpenFilters(!openFilters)} style={{ marginBottom: "1rem" }}>
                {openFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
            </Button>

            <div className="d-flex justify-content-between mb-3">
                <div className="d-flex justify-content-between mb-3">
                    <select id="clientsPerPage" className="form-select" value={pageSize} onChange={handlePageSizeChange} style={{ width: "100px" }}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={70}>70</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            <UnificarModales />
            <br />
            <p>Accion crear cotizacion</p>
            <div style={{ display: "flex", gap: "10px" }}>
                <button type="button" className="btn-shadow btn btn-warning" onClick={() => generarCotizacion()}>
                    Generar Cotización
                </button>
                <button type="button" className="btn-shadow btn btn-dark" onClick={() => fetchCotizaciones(0)}>
                    Ver Cotizaciones Confirmadas
                </button>
            </div>
            <br />

            <Collapse in={openFilters}>
                <div className="row">
                    <div className="col-md-4 mb-2">
                        <input
                            className="form-control"
                            type="text"
                            placeholder="Buscar Nombre del Evento"
                            value={filters.nombre_evento_coti}
                            onChange={(e) => handleFilterChange("nombre_evento_coti", e.target.value)}
                        />
                    </div>
                    <div className="col-md-4 mb-2">
                        <input
                            className="form-control"
                            type="text"
                            placeholder="Buscar ID Consecutivo"
                            value={filters.id_consecutivo_coti}
                            onChange={(e) => handleFilterChange("id_consecutivo_coti", e.target.value)}
                        />
                    </div>
                    <div className="col-md-4 mb-2">
                        <Select
                            className="form-control"
                            placeholder="Seleccionar Empresa"
                            options={uniqueOptions("nombre_empresa")}
                            value={filters.nombre_empresa}
                            onChange={(value) => handleFilterChange("nombre_empresa", value)}
                            isClearable
                        />
                    </div>
                    <div className="col-md-4 mb-2">
                        <Select
                            className="form-control"
                            placeholder="Seleccionar Admin"
                            options={uniqueOptions("name_admin")}
                            value={filters.name_admin}
                            onChange={(value) => handleFilterChange("name_admin", value)}
                            isClearable
                        />
                    </div>
                    <div className="col-md-4 mb-2">
                        <Select
                            className="form-control"
                            placeholder="Seleccionar Estado"
                            options={uniqueOptions("Estado_coti")}
                            value={filters.Estado_coti}
                            onChange={(value) => handleFilterChange("Estado_coti", value)}
                            isClearable
                        />
                    </div>
                </div>
            </Collapse>

            <div className="table-responsive">
                {loading ? (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <table
                        className="table table-striped table dt-responsive w-100 display text-left hoverable-table"
                        style={{ fontSize: "15px", width: "100%", textAlign: "left" }}
                    >
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th key={column.key}>{column.header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {currentData.length > 0 ? (
                                currentData.map((row, rowIndex) => (
                                    <tr key={rowIndex} onClick={() => handleRowClick(row)} style={{ cursor: "pointer" }}>
                                        {columns.map((column) => (
                                            // Renderiza cada celda de la fila
                                            <td
                                                key={column.key} // Usa la clave de la columna como clave única
                                                style={column.key === "Estado_coti" ? getEstadoStyle(row[column.key]) : {}}
                                                // Aplica estilos condicionales para la columna "Estado_coti"
                                            >
                                                {
                                                    // Renderiza el contenido de la celda según la columna
                                                    column.key === "fecha_creacion_coti"
                                                        ? formatearFechaModi(row.fecha_creacion_coti) // Formatea la fecha de creación
                                                        : column.key === "fecha_evento_coti"
                                                        ? row[column.key]
                                                            ? formatearFechaEvento(row[column.key]).substring(0, 22) // Formatea la fecha del evento
                                                            : "Fecha por defin" // Mensaje por defecto si no hay fecha
                                                        : column.key === "nombre_evento_coti"
                                                        ? row[column.key].length > 20
                                                            ? `${row[column.key].substring(0, 15)}...` // Trunca el nombre del evento si es muy largo
                                                            : row[column.key] // Mantiene el nombre completo si es corto
                                                        : column.key === "nombre_empresa"
                                                        ? row[column.key].length > 20
                                                            ? `${row[column.key].substring(0, 15)}...` // Trunca el nombre de la empresa si es muy largo
                                                            : row[column.key] // Mantiene el nombre completo si es corto
                                                        : row[column.key] // Valor por defecto para otras columnas
                                                }
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} style={{ textAlign: "center" }}>
                                        No hay datos disponibles
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="pagination-controls" style={{ textAlign: "center", marginTop: "20px" }}>
                <button className="btn btn-dark" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                    Anterior
                </button>
                <span style={{ margin: "0 1rem" }}>
                    Página {currentPage} de {totalPages}
                </span>
                <button className="btn btn-dark" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                    Siguiente
                </button>
            </div>

            <DetalleCotizacionModal open={openModal} handleClose={handleCloseModal} data={selectedData} />
        </div>
    );
};
